import { useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useStore } from '../../../../store/store';
import useContactsDownloadPreview from '../../../../hooks/useContactsDownloadPreview';
import useEnrichmentContactsDownload from '../../../../hooks/useEnrichmentContactsDownload';
import { buildDownloadUrl, DOWNLOAD_CONFIGS } from '../utils/downloadUtils';
import EnrichmentDownloadDialog from '../../../enrichment/EnrichmentDownloadDialog';

/**
 * Company-scope connector for the v3 XLSX download dialog (TD-2101).
 *
 * Binds the async-download orchestration (useEnrichmentContactsDownload) to the
 * presentational EnrichmentDownloadDialog. Mounted by CompanyDetails; opened
 * only for enrichment-enabled users (the gate lives in CompanyDetails via
 * shouldOpenEnrichmentDownloadDialog — TD-1908 regression guard).
 *
 * On open it fetches the merged download preview (POST /bulk-download-precheck
 * with `source: 'company'` and includeContacts), so the dialog shows the
 * company-download counter (A/B), the contact count / cached credits, and — since
 * TD-2527 — the "Filter by role" section. The fetch is shared with
 * ProjectContactsDownloadDialog via useContactsDownloadPreview (the BE returns
 * `role_buckets` + filtered counts for the company scope since TD-2393); it is
 * fail-open: a precheck blip degrades the counters but never blocks a legitimate
 * download (the export re-checks the limit server-side).
 *
 * On a successful binary the hook calls onComplete → we refresh the contact
 * list (so freshly-enriched email/phone statuses reveal without a manual page
 * reload — TD-2283 item 1) and close the dialog. On a failed job (AC-8 clause 5)
 * the hook calls onError → we also close.
 */
const CompanyContactsDownloadDialog = observer(function CompanyContactsDownloadDialog({
  isOpen,
  onClose,
  onDownloaded,
  companyId
}) {
  const { getToken, makeRequest } = useStore();
  const { previewLoading, dialogProps } = useContactsDownloadPreview({
    isOpen,
    source: 'company',
    sourceId: companyId,
    makeRequest
  });

  // Late-bound reset reference. The hook returns `reset`, but we need to pass
  // `onError: handleClose` INTO the hook — `handleClose` itself calls
  // `reset()`. A ref breaks the chicken-and-egg: handleClose reads the ref
  // (set below), the hook receives a stable onError, and the ref is populated
  // immediately after the hook returns.
  const resetRef = useRef(() => {});

  const handleClose = useCallback(() => {
    resetRef.current();
    onClose();
  }, [onClose]);

  // On a completed download/enrich, refetch the contact records before closing
  // so the cards pick up the now-persisted enrichment_status_email/phone and
  // reveal the found value / "Not found" per field (TD-2283 item 1). Mirrors the
  // bulk-reveal path (onBulkRevealComplete={state.loadDetails} in CompanyDetails).
  const handleComplete = useCallback(() => {
    onDownloaded?.();
    onClose();
  }, [onDownloaded, onClose]);

  const download = useEnrichmentContactsDownload({
    getToken,
    resolveBaseUrl: () =>
      buildDownloadUrl(import.meta.env.VITE_API_BASEURL, 'contacts', { companyId }),
    filename: DOWNLOAD_CONFIGS.contacts.defaultFilename,
    contentType: DOWNLOAD_CONFIGS.contacts.defaultContentType,
    linkId: DOWNLOAD_CONFIGS.contacts.linkId,
    onComplete: handleComplete,
    // AC-8 clause 5: on `failed` the dialog must close (toast already fires in
    // the hook). Plan-compliance fix from Phase 3 review.
    onError: handleClose
  });
  resetRef.current = download.reset;

  return (
    <EnrichmentDownloadDialog
      isOpen={isOpen}
      onClose={handleClose}
      variant="company"
      {...dialogProps}
      // Company scope has no dedicated A/B counter fetch any more — the A/B numbers
      // ride on the same preview; reuse the loading flag so the box still renders
      // its skeleton while the precheck is in flight (TD-2283 review item 2).
      companyDownloadsLoading={previewLoading}
      phase={download.phase}
      isDownloading={download.isDownloading}
      enrichedCount={download.enrichedCount}
      totalContacts={download.totalContacts}
      onDownload={download.startDownload}
    />
  );
});

CompanyContactsDownloadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownloaded: PropTypes.func,
  companyId: PropTypes.string
};

export default CompanyContactsDownloadDialog;
